import React from "react";
import { Box, Stack, IconButton } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { type Modifier } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type UseFormRegister } from "react-hook-form";
import { ButtonDeleteIcon } from "@ippon/shared-assets";
import { CustomTextInput, colors } from "@ippon/shared-components";

/**
 * カスタムプルダウンの各エディタが公開する命令的ハンドル。
 * 下部 FixedActionBar の「更新」から親がまとめて save() を呼ぶ。
 */
export interface PulldownEditorHandle {
  /** 未保存の変更があるか */
  isDirty: () => boolean;
  /** 変更をコミットし、失敗メッセージ配列を返す (空=成功) */
  save: () => Promise<string[]>;
  /** 削除対象 (サーバに存在し現在の編集で消された項目) の名前一覧。更新前の確認用。 */
  pendingDeletions: () => string[];
  /** 削除対象が実際に削除可能か。確認モーダルを開く前に検証する。 */
  validateDeletions?: () => Promise<string[]>;
}

// カスタムフォームと同じ並べ替え制約 (縦のみ / 親内に制限)
export const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
});
export const restrictToParentElement: Modifier = ({
  containerNodeRect,
  draggingNodeRect,
  transform,
}) => {
  if (!draggingNodeRect || !containerNodeRect) return transform;
  const next = { ...transform };
  if (draggingNodeRect.top + transform.y < containerNodeRect.top) {
    next.y = containerNodeRect.top - draggingNodeRect.top;
  }
  if (
    draggingNodeRect.bottom + transform.y >
    containerNodeRect.top + containerNodeRect.height
  ) {
    next.y =
      containerNodeRect.top +
      containerNodeRect.height -
      draggingNodeRect.bottom;
  }
  return next;
};

interface SortableTextRowProps {
  id: string;
  register: UseFormRegister<Record<string, string>>;
  onDelete: () => void;
  placeholder?: string;
  /** 削除ボタンを表示するか (権限ゲート用)。既定 true。 */
  canDelete?: boolean;
}

/**
 * カスタムフォームの SortableFieldRow と同じ行 (ドラッグハンドル + テキスト入力 + 削除)。
 * カスタムプルダウンの各エディタ (選択肢マスタ / 収入支出カテゴリ) で共用する。
 * 値は react-hook-form に `name=id` で登録される。
 */
export const SortableTextRow: React.FC<SortableTextRowProps> = ({
  id,
  register,
  onDelete,
  placeholder = "選択肢名",
  canDelete = true,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        border: "1px solid #E5E5E5",
        borderRadius: "8px",
        p: 1,
        backgroundColor: "#FAFAFA",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#999",
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
            flexShrink: 0,
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CustomTextInput
            label=""
            name={id}
            register={register}
            errors={{}}
            placeholder={placeholder}
            hasSpacing={false}
            inputWidth="100%"
            inputWidthSp="100%"
          />
        </Box>
        {canDelete && (
          <IconButton
            size="small"
            onClick={onDelete}
            title="削除"
            sx={{
              color: colors.text.secondary,
              flexShrink: 0,
              "&:hover": { color: colors.status.error },
            }}
          >
            <ButtonDeleteIcon width={14} height={16} />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
};
